import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import { User } from '../types';
import './UserAdministration.css';

const UserAdministration: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState<string | null>(null);

  // Fetch users from database on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const fetchedUsers = await apiService.getUsers();
        setUsers(fetchedUsers);
      } catch (error) {
        alert('Failed to fetch users from database. Please check the console for details.');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleUnlockUser = async (userId: string) => {
    try {
      setUnlocking(userId);
      await apiService.unlockUser(userId);
      // Refresh users list
      const fetchedUsers = await apiService.getUsers();
      setUsers(fetchedUsers);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to unlock user');
    } finally {
      setUnlocking(null);
    }
  };

  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'Active').length;
  const lockedUsers = users.filter(u => u.is_locked).length;
  const administrators = users.filter(u => u.role === 'Administrator').length;
  const appOwners = users.filter(u => u.role === 'Application Owner').length;
  const aprAnalysts = users.filter(u => u.role === 'APR Analyst').length;

  const lockedUsersList = users.filter(u => u.is_locked);

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">User Administration</h1>
            <p className="page-header__subtitle">
              Manage system users and their permissions
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Total Users</div>
          <div className="stat-number">{totalUsers}</div>
        </div>
        <div className="stat-card success">
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Active Users</div>
          <div className="stat-number">{activeUsers}</div>
        </div>
        <div className={`stat-card ${lockedUsers > 0 ? 'danger' : 'success'}`}>
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Locked Users</div>
          <div className="stat-number">{lockedUsers}</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>Administrators</div>
          <div className="stat-number">{administrators}</div>
        </div>
        <div className="stat-card info">
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>App Owners</div>
          <div className="stat-number">{appOwners}</div>
        </div>
        <div className="stat-card primary">
          <div className="stat-label" style={{ fontSize: '14px', whiteSpace: 'nowrap' }}>APR Analysts</div>
          <div className="stat-number">{aprAnalysts}</div>
        </div>
      </div>

      {/* Locked Users Section */}
      {lockedUsers > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '8px',
            padding: '20px'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#c62828',
              fontSize: '18px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>🔒</span> Locked User Accounts ({lockedUsers})
            </h3>
            <p style={{
              margin: '0 0 16px 0',
              color: '#666',
              fontSize: '14px'
            }}>
              The following users have been locked due to multiple failed login attempts. Click "Unlock" to restore access.
            </p>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>User</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Role</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Locked At</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Failed Attempts</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {lockedUsersList.map((user) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#333' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>🔒</span>
                        {user.name}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>{user.email}</td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>{user.role}</td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>
                      {user.locked_at ? new Date(user.locked_at).toLocaleString() : 'N/A'}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c62828', fontWeight: '600' }}>
                      {user.failed_login_attempts || 0}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleUnlockUser(user.id)}
                        disabled={unlocking === user.id}
                        style={{
                          padding: '8px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: 'white',
                          backgroundColor: unlocking === user.id ? '#ccc' : '#4caf50',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: unlocking === user.id ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (unlocking !== user.id) {
                            e.currentTarget.style.backgroundColor = '#43a047';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (unlocking !== user.id) {
                            e.currentTarget.style.backgroundColor = '#4caf50';
                          }
                        }}
                      >
                        {unlocking === user.id ? 'Unlocking...' : 'Unlock'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Users Table */}
      <div style={{ marginTop: '24px' }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#333'
        }}>
          All Users
        </h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading users...</div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>#</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Name</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Email</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Role</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333', borderBottom: '1px solid #ddd' }}>Account</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>{user.user_number}</td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#333', fontWeight: '500' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {user.is_locked && <span title="Account Locked">🔒</span>}
                      {user.name}
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>{user.email}</td>
                  <td style={{ padding: '12px 16px', fontSize: '14px', color: '#666' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: user.role === 'Administrator' ? '#e3f2fd' : user.role === 'APR Analyst' ? '#fff3e0' : '#f3e5f5',
                      color: user.role === 'Administrator' ? '#1565c0' : user.role === 'APR Analyst' ? '#e65100' : '#7b1fa2'
                    }}>
                      {user.role}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: user.status === 'Active' ? '#e8f5e9' : '#ffebee',
                      color: user.status === 'Active' ? '#2e7d32' : '#c62828'
                    }}>
                      {user.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    {user.is_locked ? (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        🔒 Locked
                      </span>
                    ) : (
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32'
                      }}>
                        Unlocked
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      </div>
    </div>
  );
};

export default UserAdministration;
